/**
 * reviews-lite — lazy REVIEWS.io review list.
 * Uses data-prl-sku (main identifier) and optional data-prl-skus (multi-variant).
 */
(() => {
  const API = 'https://api.reviews.io/reviews';

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function stars(rating) {
    const rounded = Math.max(0, Math.min(5, Math.round(Number(rating) || 0)));
    return '★★★★★'.slice(0, rounded) + '☆☆☆☆☆'.slice(0, 5 - rounded);
  }

  function toText(value) {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (value && typeof value === 'object') {
      return (
        toText(value.text)
        || toText(value.content)
        || toText(value.body)
        || toText(value.name)
        || ''
      );
    }
    return '';
  }

  function formatDate(date) {
    if (!date) return '';
    const parsed = new Date(date);
    if (Number.isNaN(parsed.getTime())) return date;
    return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  }

  function ratingPercent(rating) {
    return Math.max(0, Math.min(100, (Number(rating || 0) / 5) * 100));
  }

  function normalizeOptions(raw) {
    if (!Array.isArray(raw)) return [];
    return raw
      .map((option) => {
        if (typeof option === 'string') return option;
        if (option && typeof option === 'object') return toText(option.label || option);
        return '';
      })
      .filter(Boolean);
  }

  function isRatingAttribute(type, value) {
    if (type && /rating|range|star/i.test(type)) return true;
    const num = Number(value);
    return !Number.isNaN(num) && num >= 1 && num <= 5;
  }

  function normalizeAttribute(attr) {
    const label = toText(attr.label || attr.question || '');
    const value = toText(attr.value || attr.average_rating || '');
    const type = toText(attr.type || '');
    const attributeId = toText(attr.attribute_id || attr.id || '');
    const options = normalizeOptions(attr.options || attr.values);
    const numericValue = Number(value);
    const isCenteredRange = type === 'centered-range' && options.length >= 2;
    const isRating = !isCenteredRange && isRatingAttribute(type, value);
    return { label, value, type, attributeId, options, numericValue, isCenteredRange, isRating, displayValue: '' };
  }

  function buildAttributeLookup(filters) {
    const lookup = {};
    const items = filters && filters.review_attributes;
    if (!Array.isArray(items)) return lookup;

    items.forEach((item) => {
      lookup[String(item.id)] = {
        label: toText(item.label),
        type: toText(item.type),
        buckets: Array.isArray(item.values) ? item.values : []
      };
    });
    return lookup;
  }

  function resolveLabelFromLookup(attributeId, numericValue, lookup) {
    const entry = lookup[String(attributeId)];
    if (!entry || !Array.isArray(entry.buckets)) return '';

    const num = Number(numericValue);
    if (Number.isNaN(num)) return '';

    for (const bucket of entry.buckets) {
      if (!bucket || !Array.isArray(bucket.values)) continue;
      if (bucket.values.map(Number).includes(num)) {
        return toText(bucket.label);
      }
    }

    return '';
  }

  function enrichAttribute(attr, lookup, { roundValue = false } = {}) {
    if (!attr.isCenteredRange) return attr;

    const lookupValue = roundValue ? Math.round(attr.numericValue) : attr.numericValue;
    const displayValue = resolveLabelFromLookup(attr.attributeId, lookupValue, lookup);

    return {
      ...attr,
      displayValue: displayValue || attr.value
    };
  }

  function centeredRangePosition(value, min = 1, max = 5) {
    const num = Number(value);
    if (Number.isNaN(num)) return 0;
    return Math.max(0, Math.min(100, ((num - min) / (max - min)) * 100));
  }

  function attributeRangeLabelsMarkup(options) {
    if (options.length === 3) {
      return `
        <span class="prl-range__label">${escapeHtml(options[0])}</span>
        <span class="prl-range__label prl-range__label--center">${escapeHtml(options[1])}</span>
        <span class="prl-range__label prl-range__label--end">${escapeHtml(options[2])}</span>
      `;
    }

    return options.map((option, index) => {
      let positionClass = '';
      if (index === options.length - 1) positionClass = ' prl-range__label--end';
      else if (index > 0) positionClass = ' prl-range__label--center';
      return `<span class="prl-range__label${positionClass}">${escapeHtml(option)}</span>`;
    }).join('');
  }

  function attributeRangeMarkup(attr, compact) {
    const position = centeredRangePosition(attr.numericValue);
    const description = attr.displayValue || String(attr.numericValue);
    const compactClass = compact ? ' prl-range--compact' : '';

    return `
      <div class="prl-range${compactClass}" role="img" aria-label="${escapeHtml(attr.label)}: ${escapeHtml(description)}">
        <div class="prl-range__track">
          <span class="prl-range__marker" style="left: ${position}%;"></span>
        </div>
        <div class="prl-range__labels">
          ${attributeRangeLabelsMarkup(attr.options)}
        </div>
      </div>
    `;
  }

  function keepAttribute(attr) {
    if (!attr.label) return false;
    if (attr.isCenteredRange) return !Number.isNaN(attr.numericValue) && attr.numericValue > 0;
    if (attr.isRating) return attr.value && !Number.isNaN(attr.numericValue);
    return Boolean(attr.value);
  }

  function normalizeStatsAttributes(json, lookup) {
    const attrs = json && json.stats && json.stats.attributes;
    if (!Array.isArray(attrs)) return [];
    return attrs
      .map(normalizeAttribute)
      .filter(keepAttribute)
      .map((attr) => enrichAttribute(attr, lookup, { roundValue: true }));
  }

  function normalizeReviewAttributes(review, lookup) {
    const productAttrs = Array.isArray(review.attributes)
      ? review.attributes.map(normalizeAttribute).filter(keepAttribute).map((attr) => enrichAttribute(attr, lookup))
      : [];
    const authorAttrs = review.author && Array.isArray(review.author.attributes)
      ? review.author.attributes.map(normalizeAttribute).filter(keepAttribute).map((attr) => enrichAttribute(attr, lookup))
      : [];
    return productAttrs.concat(authorAttrs);
  }

  function normalizeReview(review, lookup) {
    return {
      author: toText((review.author && review.author.name) || review.reviewer || review.author_name) || 'Verified customer',
      rating: Number(review.rating || review.rating_value || 0),
      title: toText(review.title || ''),
      body: toText(review.comments || review.body || review.content || review.review || ''),
      date: toText(review.date_created || review.date || review.created_at || ''),
      verified: Number(review.verified || 0) === 1,
      timeAgo: toText(review.time_ago || ''),
      attributes: normalizeReviewAttributes(review, lookup)
    };
  }

  function readAttr(node, name) {
    if (!node) return '';
    const value = node.getAttribute(name);
    return value == null ? '' : String(value).trim();
  }

  function primarySku(node) {
    return readAttr(node, 'data-prl-sku');
  }

  function allSkus(node) {
    return readAttr(node, 'data-prl-skus');
  }

  /** SKU sent to REVIEWS.io: single-SKU products use main identifier only. */
  function apiSku(node, preferPrimary) {
    const main = primarySku(node);
    const all = allSkus(node);
    const multi = node && node.getAttribute('data-prl-multi-variant') === 'true';

    if (preferPrimary || !multi) {
      return main || all;
    }
    return all || main;
  }

  function buildUrl(store, sku, page, perPage) {
    const url = new URL(API);
    url.searchParams.set('store', store);
    url.searchParams.set('type', 'product_review');
    url.searchParams.set('per_page', String(perPage));
    url.searchParams.set('page', String(page));
    url.searchParams.set('include_subrating_breakdown', 'true');
    if (sku) url.searchParams.set('sku', sku);
    return url.toString();
  }

  function shouldShowAttributes(root) {
    return readAttr(root, 'data-prl-show-attributes') !== 'false';
  }

  function attributeSummaryMarkup(attr) {
    if (attr.isCenteredRange) {
      return `
        <div class="prl-attribute prl-attribute--range">
          <div class="prl-attribute__label">${escapeHtml(attr.label)}</div>
          ${attributeRangeMarkup(attr, false)}
        </div>
      `;
    }

    if (attr.isRating) {
      const display = attr.numericValue.toFixed(1);
      const pct = ratingPercent(attr.numericValue);
      return `
        <div class="prl-attribute prl-attribute--rating">
          <div class="prl-attribute__label-row">
            <span class="prl-attribute__label">${escapeHtml(attr.label)}</span>
            <span class="prl-attribute__value">${escapeHtml(display)}</span>
          </div>
          <div class="prl-attribute__bar" style="--rating-percent: ${pct}%;" role="img" aria-label="${escapeHtml(attr.label)}: ${escapeHtml(display)} out of 5">
            <span class="prl-attribute__bar-fill"></span>
          </div>
        </div>
      `;
    }

    return `
      <div class="prl-attribute prl-attribute--text">
        <span class="prl-attribute__label">${escapeHtml(attr.label)}</span>
        <span class="prl-attribute__value">${escapeHtml(attr.value)}</span>
      </div>
    `;
  }

  function reviewAttributesMarkup(attributes) {
    if (!attributes.length) return '';

    return `
      <dl class="prl-review__attributes">
        ${attributes.map((attr) => {
          if (attr.isCenteredRange) {
            return `
              <div class="prl-review__attribute prl-review__attribute--range">
                <dt>${escapeHtml(attr.label)}</dt>
                <dd>${attributeRangeMarkup(attr, true)}</dd>
              </div>
            `;
          }

          if (attr.isRating) {
            const display = attr.numericValue.toFixed(1);
            const pct = ratingPercent(attr.numericValue);
            return `
              <div class="prl-review__attribute prl-review__attribute--rating">
                <dt>${escapeHtml(attr.label)}</dt>
                <dd>
                  <span class="prl-review__attribute-value">${escapeHtml(display)}</span>
                  <span class="prl-attribute__bar prl-attribute__bar--compact" style="--rating-percent: ${pct}%;" aria-hidden="true">
                    <span class="prl-attribute__bar-fill"></span>
                  </span>
                </dd>
              </div>
            `;
          }

          return `
            <div class="prl-review__attribute">
              <dt>${escapeHtml(attr.label)}</dt>
              <dd>${escapeHtml(attr.value)}</dd>
            </div>
          `;
        }).join('')}
      </dl>
    `;
  }

  function reviewMarkup(review, showAttributes) {
    const attributesMarkup = showAttributes ? reviewAttributesMarkup(review.attributes) : '';

    return `
      <li class="prl-review">
        <div class="prl-review__head">
          <div class="prl-review__identity">
            <span class="prl-review__author">${escapeHtml(review.author)}</span>
            ${review.verified ? '<div class="prl-review__verified"><span class="prl-review__icon">✔</span>Verified Customer</div>' : ''}
          </div>
        </div>
        <div class="prl-review__rating" aria-label="${review.rating} out of 5 stars">${stars(review.rating)}</div>
        ${review.title ? `<h3 class="prl-review__title">${escapeHtml(review.title)}</h3>` : ''}
        ${review.body ? `<p class="prl-review__body">${escapeHtml(review.body)}</p>` : ''}
        ${attributesMarkup}
        <div class="prl-review__footer">
          <span class="prl-review__date">${escapeHtml(review.timeAgo || formatDate(review.date))}</span>
        </div>
      </li>
    `;
  }

  function updateAttributesSummary(root, attributes) {
    const container = root.querySelector('[data-prl-attributes]');
    if (!container || !shouldShowAttributes(root)) return;

    if (!attributes.length) {
      container.hidden = true;
      container.innerHTML = '';
      return;
    }

    container.hidden = false;
    container.innerHTML = `<div class="prl-attributes">${attributes.map(attributeSummaryMarkup).join('')}</div>`;
  }

  function updateBlockSummary(root, average, count) {
    const summary = root.querySelector('[data-prl-summary]');
    if (!summary) return;
    const avg = Number(average || 0);
    const n = Number(count || 0);
    if (n === 0) return;

    summary.hidden = false;
    const avgEl = summary.querySelector('[data-prl-average]');
    const countEl = summary.querySelector('[data-prl-count]');
    const based = summary.querySelector('.prl-reviews__based');
    if (avgEl) {
      avgEl.hidden = false;
      avgEl.textContent = avg.toFixed(1);
    }
    if (countEl) countEl.textContent = String(n);
    if (based) based.innerHTML = `Based on <span data-prl-count>${n}</span> reviews`;
    const starsWrap = summary.querySelector('.prl-rating__stars');
    if (starsWrap) {
      starsWrap.style.setProperty('--rating-percent', `${Math.max(0, Math.min(100, (avg / 5) * 100))}%`);
    }
  }

  function showEmpty(root) {
    const state = root.querySelector('[data-prl-state]');
    const list = root.querySelector('[data-prl-list]');
    const moreBtn = root.querySelector('[data-prl-load-more]');
    const summary = root.querySelector('[data-prl-summary]');

    if (summary) {
      summary.hidden = false;
      const avgEl = summary.querySelector('[data-prl-average]');
      const starsWrap = summary.querySelector('.prl-rating__stars');
      const based = summary.querySelector('.prl-reviews__based');
      if (avgEl) avgEl.hidden = true;
      if (starsWrap) starsWrap.style.setProperty('--rating-percent', '0%');
      if (based) based.textContent = 'Not yet reviewed';
    }

    if (state) {
      state.hidden = false;
      state.textContent = 'Be the first to share your thoughts on this product.';
    }
    if (list) {
      list.hidden = true;
      list.innerHTML = '';
    }
    if (moreBtn) moreBtn.hidden = true;

    const attributes = root.querySelector('[data-prl-attributes]');
    if (attributes) {
      attributes.hidden = true;
      attributes.innerHTML = '';
    }
  }

  async function fetchPage(store, sku, page, perPage) {
    const response = await fetch(buildUrl(store, sku, page, perPage), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('fetch failed');
    const json = await response.json();
    return {
      json,
      reviews: Array.isArray(json.reviews) ? json.reviews : [],
      perPage
    };
  }

  async function loadReviews(root, sku) {
    const store = readAttr(root, 'data-prl-store-id');
    const perPage = Number(root.dataset.prlPerPage || 10);
    return fetchPage(store, sku, 1, perPage);
  }

  async function hydrateBlock(root) {
    const state = root.querySelector('[data-prl-state]');
    const list = root.querySelector('[data-prl-list]');
    const moreBtn = root.querySelector('[data-prl-load-more]');
    const store = readAttr(root, 'data-prl-store-id');
    let sku = apiSku(root, false);

    if (!store || !sku) {
      if (state) state.textContent = 'Reviews are unavailable for this product.';
      return;
    }

    try {
      let payload;
      try {
        payload = await loadReviews(root, sku);
      } catch (_multiErr) {
        const fallback = primarySku(root);
        if (fallback && fallback !== sku) {
          sku = fallback;
          payload = await loadReviews(root, sku);
        } else {
          throw _multiErr;
        }
      }

      const { json, reviews, perPage } = payload;
      const attributeLookup = buildAttributeLookup(json.filters);
      root._prlAttributeLookup = attributeLookup;
      const apiCount = Math.max(Number(json.review_count || 0), Number(json.results_count || 0));
      const apiAvg = Number(json.average_rating || 0);

      if (reviews.length === 0 && apiCount === 0) {
        showEmpty(root);
        return;
      }

      updateBlockSummary(root, apiAvg, apiCount || reviews.length);
      updateAttributesSummary(root, normalizeStatsAttributes(json, attributeLookup));

      const showAttributes = shouldShowAttributes(root);

      if (state) state.hidden = true;
      if (list) {
        list.hidden = false;
        list.innerHTML = reviews.map((r) => reviewMarkup(normalizeReview(r, attributeLookup), showAttributes)).join('');
      }

      root._prlPage = 1;
      root._prlPerPage = perPage;
      root._prlSku = sku;
      root._prlHasMore = reviews.length >= perPage;

      if (moreBtn) {
        moreBtn.hidden = !root._prlHasMore;
        if (!moreBtn.dataset.prlBound) {
          moreBtn.dataset.prlBound = 'true';
          moreBtn.addEventListener('click', () => loadMore(root));
        }
      }
    } catch (_err) {
      if (state) {
        state.hidden = false;
        state.textContent = 'Reviews are temporarily unavailable.';
      }
    }
  }

  async function loadMore(root) {
    const list = root.querySelector('[data-prl-list]');
    const moreBtn = root.querySelector('[data-prl-load-more]');
    if (!list || !root._prlHasMore) return;

    const store = readAttr(root, 'data-prl-store-id');
    const sku = root._prlSku || apiSku(root, false);

    moreBtn.disabled = true;
    try {
      const nextPage = (root._prlPage || 1) + 1;
      const { reviews, perPage } = await fetchPage(store, sku, nextPage, root._prlPerPage || 10);
      if (reviews.length > 0) {
        const showAttributes = shouldShowAttributes(root);
        const attributeLookup = root._prlAttributeLookup || {};
        list.insertAdjacentHTML(
          'beforeend',
          reviews.map((r) => reviewMarkup(normalizeReview(r, attributeLookup), showAttributes)).join('')
        );
      }
      root._prlPage = nextPage;
      root._prlHasMore = reviews.length >= perPage;
      moreBtn.hidden = !root._prlHasMore;
    } catch (_err) {
      /* keep existing list */
    }
    moreBtn.disabled = false;
  }

  function boot() {
    const blocks = document.querySelectorAll('[data-prl-reviews]');
    if (blocks.length === 0) return;

    if (!('IntersectionObserver' in window)) {
      blocks.forEach((block) => hydrateBlock(block));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        hydrateBlock(entry.target);
      });
    }, { rootMargin: '200px 0px' });

    blocks.forEach((block) => observer.observe(block));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
